import { computed } from 'vue';
import { useStore } from 'vuex';
import { useAnimatedNumber } from 'JIX/animatedNumber.js';
import { getNumberFormatter, normalizeQuotes } from 'JIX/utils.js';
import { __, __nx } from 'JIX/gettext.js';
import { PART_TIME_MAX_HOURS } from 'JIX/components/jobsearch/constants.js';
import cloneDeep from 'lodash/cloneDeep';

/** Maps a parameter such that its value when read is taken from the current search query
 * (so that the filters and sorting is correctly shown when loading the page)
 * And when the parameter is set, it will be stored in the vuex store, and used in the next search query*/
export function mapParam(store, key) {
    return computed({
        get: () => store.getters.workingQuery[key],
        set: (value) => {
            store.commit('setParam', { key, value });
        },
    });
}

export function useTwoWayBinding(stateKey) {
    const store = useStore();
    return computed({
        get: () => store.state[stateKey],
        set: (value) => store.commit('updateState', { stateKey, value }),
    });
}

export function computeCumulativeSums(arr, offset = 0) {
    let sum = offset;
    return arr.map(num => sum += num);
}

export function queryIsNonEmpty(query) {
    for (const [_type, { parameters } ] of Object.entries(query)) {
        if (parameters.length) { return true; }
    }
    return false;
}

export function parseQstring(query) {
    // When updating this function, remember to make the same changes to
    // JIX::Jobsearch::String->parse_qstring as well.

    const res = { keywords: [], mustwords: [], notwords: [] };

    if (!query) {
        return res;
    }

    query = normalizeQuotes(query);

    let rest = query;
    rest = rest.replaceAll('"', "'");
    rest = rest.replaceAll(/[,;]/g, ' ');

    const numQuotes = (rest.match(/'/g) || []).length;
    if (numQuotes % 2) {
        rest += "'";
    }

    const oper = "\\+|\\-|!|";
    const word = "(?:\\'[^\\']*\\')|\\S+";

    const regex = new RegExp(`^\\s*(${oper})(${word})(.*)$`);

    let match;
    while ((match = regex.exec(rest)) !== null) {
        const t = match[1] || '';
        const word = _normalizeQueryWord(match[2]);
        rest = match[3];

        if (!word) {
            continue;
        }

        if (t === '') {
            res.keywords.push(word);
        } else if (t === '-' || t === '!') {
            res.notwords.push(word);
        } else if (t === '+') {
            res.mustwords.push(word);
        }
    }

    return res;
}

function _normalizeQueryWord(word) {
    return word.trim()
        .replace(/^\s*'(.*?)'\s*$/, '$1')
        .replace(/\s+/g, ' ');
}

export function buildQstringParts(data) {
    const parts = [];
    parts.push(...(data.keywords  || []).map(x => x.includes(' ') ? `"${x}"`  : x));
    parts.push(...(data.mustwords || []).map(x => x.includes(' ') ? `+"${x}"` : `+${x}`));
    parts.push(...(data.notwords  || []).map(x => x.includes(' ') ? `-"${x}"` : `-${x}`));
    return parts;
}

export function buildQstring(data) {
    return buildQstringParts(data).join(" ");
}

export function splitQstring(query) {
    return buildQstringParts(parseQstring(query));
}

export function useSearchButtonText({ hitcount }) {
    const store = useStore();
    if (store.state.viewMode !== 'list' && store.state.viewMode !== 'map') {
        return __('Søg');
    }
    const numberFormatter = getNumberFormatter();

    const { formattedDisplayNumber } = useAnimatedNumber({
        number: hitcount,
        defaultValue: __('Søg'),
        formatter: (n) => {
            return __nx("Vis {count} job", "Vis {count} job", n, { count: numberFormatter.format(n) });
        },
        duration: 500,
        easingFunction: function easeInOutCubic(x) { // https://easings.net/#easeInOutCubic
            return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2;
        },
        skipInitial: true,
    });
    return formattedDisplayNumber;
}

export function processParams(params) {
    let processedParams = cloneDeep(params);
    const hasRadius = typeof processedParams.radius === 'number';
    const hasAddress = processedParams.address?.trim();
    if (!hasRadius || !hasAddress) {
        delete processedParams.address;
        delete processedParams.radius;
    }

    //Searching for super categories by itself is not shown in the filters, so we disable it
    if ((!processedParams.subid || processedParams.subid.length === 0) && processedParams.supid){
        delete processedParams.supid;
    }

    //Remove bad input in part time min and max hours
    if (processedParams.workinghours_min && processedParams.workinghours_max && processedParams.workinghours_min > processedParams.workinghours_max){
        delete processedParams.workinghours_min;
        delete processedParams.workinghours_max;
    }
    if (processedParams.workinghours_min && (processedParams.workinghours_min < 1 || processedParams.workinghours_min > PART_TIME_MAX_HOURS)){
        delete processedParams.workinghours_min;
    }
    if (processedParams.workinghours_max && (processedParams.workinghours_max < 1 || processedParams.workinghours_max > PART_TIME_MAX_HOURS)){
        delete processedParams.workinghours_max;
    }

    //Remove bad input in archive search
    if (processedParams.mindate && processedParams.maxdate && processedParams.mindate > processedParams.maxdate){
        delete processedParams.mindate;
        delete processedParams.maxdate;
    }

    if ('q' in processedParams && Array.isArray(processedParams.q)) {
        processedParams.q = processedParams.q.join(" ");
    }
    return processedParams;
}
